import { Events, on } from './events.js';

class StoreShippingBar extends HTMLElement {
  static get observedAttributes() { return ['data-threshold-cents','data-threshold-major','data-debug']; }
  connectedCallback() {
    try {
      this.__lastCart = null;
      this.#dlog('connected');
      // Initial render from global state; if absent, try fetching
      const initial = this.#getCart();
      if (initial) {
        this.#dlog('initial cart from global', { total: initial.total_price, count: initial.item_count });
        this.#renderFrom(initial);
      } else {
        this.#dlog('no global cart; fetching /cart.js');
        this.#fetchAndRender();
      }
      // Listen to canonical cart event
      this.__un = on(Events.cart.update, (ev) => {
        try {
          const cart = (ev && ev.detail && ev.detail.cart) || this.#getCart();
          this.#dlog('event: store:cart:update', { hasPayload: !!(ev && ev.detail && ev.detail.cart), total: cart && cart.total_price });
          this.#renderFrom(cart);
        } catch (_) {}
      });
      // Also listen to global mirror
      try {
        this.__onCartState = (ev) => { try { const cart = ev && ev.detail && ev.detail.cart; this.#dlog('event: cartstate:change', { total: cart && cart.total_price }); this.#renderFrom(cart || this.#getCart()); } catch (_) {} };
        document.addEventListener('cartstate:change', this.__onCartState, true);
      } catch (_) {}
    } catch (_) {}
  }
  disconnectedCallback() {
    try { this.__un && this.__un(); } catch (_) {}
    try { this.__onCartState && document.removeEventListener('cartstate:change', this.__onCartState, true); } catch (_) {}
  }
  attributeChangedCallback() { try { this.#dlog('attributes changed'); this.#renderFrom(this.__lastCart || this.#getCart()); } catch (_) {} }

  #getCart() {
    try { return (window.store && window.store.cartState) || null; } catch (_) { return null; }
  }

  async #fetchAndRender() {
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      const cart = await res.json();
      this.#dlog('fetched /cart.js', { total: cart && cart.total_price });
      this.#renderFrom(cart);
    } catch (e) { this.#dlog('fetch error', e && e.message); }
  }

  #formatMoney(cents) {
    try {
      const value = typeof cents === 'number' ? Math.round(cents) : parseInt(String(cents||0), 10) || 0;
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        const fmt = window.Shopify.money_format || undefined;
        return window.Shopify.formatMoney(value, fmt);
      }
      return (value/100).toFixed(2);
    } catch (_) {
      try { return (Number(cents||0)/100).toFixed(2); } catch (__) { return String(cents); }
    }
  }

  #renderFrom(cart) {
    try {
      this.__lastCart = cart || null;
      const subtotal = cart && cart.total_price ? Number(cart.total_price) : 0;
      // Resolve threshold (cents preferred, fallback to major units)
      const threshold = this.#resolveThreshold();
      const prog = this.querySelector('[data-role="shipping-progress"]');
      const text = this.querySelector('[data-role="shipping-text"]') || this.querySelector('.text-xs');
      if (!Number.isFinite(threshold) || threshold <= 0) {
        this.#dlog('no valid threshold; skip update', {
          attrs: (() => { try { return this.getAttributeNames().reduce((a,k)=>{a[k]=this.getAttribute(k);return a;},{}); } catch(_) { return null; } })(),
          dataset: (() => { try { return { thresholdCents: this.dataset.thresholdCents, thresholdMajor: this.dataset.thresholdMajor }; } catch(_) { return null; } })()
        });
        if (prog) prog.style.width = '0%';
        return;
      }
      const remaining = Math.max(0, threshold - subtotal);
      const pct = Math.min(100, Math.round((subtotal * 100) / threshold));
      this.#dlog('render', { subtotal, threshold, remaining, pct });
      if (prog) prog.style.width = `${pct}%`;
      if (text) {
        const youAre = this.getAttribute('data-text-you-are') || 'You are';
        const awayFrom = this.getAttribute('data-text-away-from') || 'Away from';
        const youHave = this.getAttribute('data-text-you-have') || 'You have';
        const freeGen = this.getAttribute('data-text-free-shipping-gen') || this.getAttribute('data-text-free-shipping') || 'FREE SHIPPING';
        const freeAcc = this.getAttribute('data-text-free-shipping-acc') || this.getAttribute('data-text-free-shipping') || 'FREE SHIPPING';
        if (remaining > 0) {
          text.innerHTML = `${this.#escape(youAre)} <strong>${this.#formatMoney(remaining)}</strong> ${this.#escape(awayFrom)} <strong>${this.#escape(freeGen)}</strong>`;
        } else {
          text.innerHTML = `${this.#escape(youHave)} <strong>${this.#escape(freeAcc)}</strong>`;
        }
      }
    } catch (_) {}
  }

  #escape(s) {
    try { const d = document.createElement('div'); d.textContent = String(s || ''); return d.innerHTML; } catch (_) { return String(s || ''); }
  }

  #resolveThreshold() {
    try {
      // Priority: data-threshold-cents attr → dataset.thresholdCents → data-threshold-major attr → dataset.thresholdMajor
      let centsRaw = this.getAttribute('data-threshold-cents');
      if (centsRaw == null || centsRaw === '') { try { centsRaw = this.dataset && this.dataset.thresholdCents; } catch(_) { centsRaw = null; } }
      if (centsRaw == null || centsRaw === '') { try { const el = this.querySelector('[data-threshold-cents]') || this.closest('[data-threshold-cents]'); if (el) centsRaw = el.getAttribute('data-threshold-cents'); } catch(_) {} }
      let cents = parseInt(String(centsRaw || ''), 10);
      if (Number.isFinite(cents) && cents > 0) return cents;
      let majorRaw = this.getAttribute('data-threshold-major');
      if (majorRaw == null || majorRaw === '') { try { majorRaw = this.dataset && this.dataset.thresholdMajor; } catch(_) { majorRaw = null; } }
      if (majorRaw == null || majorRaw === '') { try { const el = this.querySelector('[data-threshold-major]') || this.closest('[data-threshold-major]'); if (el) majorRaw = el.getAttribute('data-threshold-major'); } catch(_) {} }
      const major = parseFloat(String(majorRaw || '0'));
      if (Number.isFinite(major) && major > 0) return Math.round(major * 100);
      return NaN;
    } catch (_) { return NaN; }
  }

  #dlog(...args) {
    try {
      const enabled = this.hasAttribute('data-debug') || (window && window.__SHIPPING_BAR_DEBUG__ === true) || (window && window.store && window.store.debug);
      if (!enabled) return;
      console.debug('[shipping-bar]', ...args);
    } catch (_) {}
  }
}

try { if (!customElements.get('store-shipping-bar')) customElements.define('store-shipping-bar', StoreShippingBar); } catch (_) {}



